<template>
  <Transition name="modal" appear>
    <div v-if="active" class="modal fixed inset-0 z-50 flex items-center justify-center p-4">
      <!-- Modern backdrop with blur effect -->
      <div 
        class="modal-background absolute inset-0 bg-black/40 dark:bg-black/60 backdrop-blur-sm transition-all duration-300" 
        @click="$emit('close')"
      ></div>

      <!-- Modern dialog card -->
      <div
        class="modal-card relative bg-white/95 dark:bg-stone-900/95 backdrop-blur-xl rounded-lg 
               shadow-2xl dark:shadow-stone-950/50 max-w-lg md:max-w-2xl lg:max-w-4xl w-fit min-w-80 max-h-[90vh] 
               border border-stone-200/50 dark:border-stone-700/50 overflow-hidden transform transition-all duration-300">
        
        <!-- Enhanced Header -->
        <header class="modal-card-head px-6 py-4 bg-gradient-to-r from-stone-50/80 to-stone-100/80 
                       dark:from-stone-800/80 dark:to-stone-750/80 backdrop-blur-sm
                       border-b border-stone-200/60 dark:border-stone-700/60 flex items-center justify-between relative z-20">
          <h3 class="text-xl font-semibold text-stone-900 dark:text-stone-50 tracking-tight">
            <slot name="title">{{ title }}</slot>
          </h3>
          <div class="flex items-center gap-2">
            <slot name="header-actions"></slot>
            <button @click="$emit('close')"
              class="group p-2 rounded-lg text-stone-400 hover:text-stone-600 dark:text-stone-500 dark:hover:text-stone-300 
                     hover:bg-stone-200/50 dark:hover:bg-stone-700/50 transition-all duration-200 transform hover:scale-110">
              <i class="fas fa-times text-sm group-hover:rotate-90 transition-transform duration-200"></i>
            </button>
          </div>
        </header>

        <section class="modal-card-body p-4 overflow-y-auto max-h-[calc(90vh-180px)] 
                        ">
          <slot></slot>
        </section>

        <footer v-if="hasFooter"
          class="modal-card-foot p-4 flex justify-end gap-3">
          <slot name="footer">
            <button v-if="cancelText" @click="$emit('cancel')" class="btn btn-secondary">
              {{ cancelText }}
            </button>
            <button v-if="confirmText" @click="$emit('confirm')" class="btn btn-primary">
              {{ confirmText }}
            </button>
          </slot>
        </footer>
      </div>
    </div>
  </Transition>
</template>

<script setup>
import { onMounted, onBeforeUnmount, watch } from 'vue';

const props = defineProps({
  active: { type: Boolean, default: false },
  title: { type: String, default: '' },
  confirmText: { type: String, default: '' },
  cancelText: { type: String, default: '' },
  hasFooter: { type: Boolean, default: true }
});

const emit = defineEmits(['close', 'cancel', 'confirm']);

const handleEscape = (e) => {
  if (e.key === 'Escape' && props.active) {
    emit('close');
  }
};

const addListeners = () => {
  document.addEventListener('keydown', handleEscape);
  document.body.classList.add('overflow-hidden');
};

const removeListeners = () => {
  document.removeEventListener('keydown', handleEscape);
  document.body.classList.remove('overflow-hidden');
};

onMounted(() => {
  if (props.active) addListeners();
});

onBeforeUnmount(() => {
  removeListeners();
});

watch(() => props.active, (newValue) => {
  if (newValue) {
    addListeners();
  } else {
    removeListeners();
  }
});
</script>
